import { useCallback } from 'react';

import { useFormFieldsContext } from '@spotify/confidence-core-react';
import { useGetFactTableLazyQuery } from '@spotify/confidence-graphql';
import {
  MeasurementFormValues,
  useDeriveMeasurementNameExtension,
} from '@spotify/plugin-confidence-metrics-react';

// Returns a callback that derives a measurement display name from a
// fact table name. Uses the derive-measurement-name extension (if
// available) to generate a human-readable name from the fact table
// description. Without an extension, this is a no-op.
export const useDeriveMeasurementName = () => {
  const { setFieldValue } = useFormFieldsContext<MeasurementFormValues>();
  const extension = useDeriveMeasurementNameExtension();
  const [fetchFactTable] = useGetFactTableLazyQuery();

  const deriveName = useCallback(
    async (factTableName: string) => {
      if (!extension) return;

      const { data } = await fetchFactTable({
        variables: { name: factTableName },
      });

      const description =
        data?.factTable?.__typename === 'MetricsV1FactTable'
          ? data.factTable.description
          : undefined;

      if (!description) return;

      const name = await extension.deriveName(description);

      if (name) {
        setFieldValue('displayName', name);
      }
    },
    [extension, fetchFactTable, setFieldValue],
  );

  return { deriveName };
};
